import { Component, OnInit, Output, EventEmitter, ViewChild, Input, OnChanges, SimpleChanges, TemplateRef, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColumnDef } from '../data-table/data-table.model';
import { StudentMaintenanceService } from '../../_services/student-maintenance/student-maintenance.service';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { SearchStudent } from './../../_models/search-student/search-student.model';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { StudentService } from './../../_services/student/student.service';
import { GenerateStudentAccessModalData } from '../../_models/grant-student-access/generate-student-access-modal-data.model';
import { GenerateStudentAccessModalComponent } from '../../students/generate-student-access-modal/generate-student-access-modal.component';
import { ManualAddModalComponent } from '../../students/add-student/manual-add-modal/manual-add-modal.component';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { MatDialog } from '@angular/material/dialog';
import { BrandFilterService } from '../../_services/brand-filter.service';
import { BrandInfo } from '../../_models/reports/brand-info.model';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FilterSpec, FilterState } from '../filter-ux/filter-state';
import { ScreenFilterUx } from '../filter-ux/screen-filter-ux';
import { ClientSuggestionsService } from '../typeahead/client-suggestions.service';

@Component({
    selector: 'student-search',
    templateUrl: './student-search.component.html',
    styleUrls: ['./student-search.component.css'],
    standalone: false
})
export class StudentSearchComponent implements OnInit, OnChanges {
  @Input() headerText: string = "";
  @Input() clientId: number;
  @Input() triggerSearchOnLoad: boolean = false;
  @Input() showAddButton: boolean = false;
  @Input() showFilters: boolean = true;
  @Input() showClientFilter: boolean = true;
  @Input() allocationSearch: boolean = false;
  @Input() excludeStudentIds: number[];
  @Input() injectedShowSearch: boolean = false;
  @Input() showPaginator: boolean = true;
  /**
   * Remember-my-filters and filter state in the URL, while filters.learner-search is on. Only the
   * learner search page opts in; the component embedded in a dialog or a client page must not rewrite
   * its host's URL.
   */
  @Input() filterUx: boolean = false;
  @Output() addStudent = new EventEmitter<SearchStudent>();

  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;

  public columns: ColumnDef<SearchStudent>[] = [];

  public searchStudentResults: Array<SearchStudent> = new Array<SearchStudent>();
  public filteredStudents: Array<SearchStudent> = new Array<SearchStudent>();
  public searchClicked: boolean = false;
  public hasStudentViewPermission: boolean = false;

  public showGrantStudentAccess: boolean = false;
  public hasGenerateStudentAccessPermission: boolean = false;
  public hasStudentSearchClientFilterPermission: boolean = false;

  public selectedStudentId: number = 0;
  public selectedStudentName: string = "";
  public showSignUpEmailButton: boolean = false;

  public unallocatedOnlyFilter: boolean;

  public searchText: string = "";
  public clientFilter: string = "";
  public searchPlaceholder: string = "";
  public studentTooltip: string = "";

  /** Selected brands; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** The user's brands; the Brand column shows only when there is more than one. */
  public brands: BrandInfo[] = [];

  /** For the template's `*appFeatureSwitch`. */
  public readonly featureKeys = FeatureSwitchKeys;
  /**
   * The `filters.learner-search` switch, resolved once on load. Off (the default, and on any failure):
   * no brand filter, no Brand column, no active-booking filter, and no new keys in the search request.
   */
  public filtersEnabled: boolean = false;
  /** Has-an-active-booking filter: true = yes, false = no, null = any. */
  public hasActiveBooking: boolean | null = null;
  /** Picked key stages (the Orbit order's key stage, e.g. KS3); empty means any. */
  public keyStages: string[] = [];
  /** The key stages on the caller's cached bookings, as select items; loaded only while the switch is on. */
  public keyStageOptions: { keyStage: string }[] = [];
  /** search.autocomplete.learner-search: the client filter suggests clients. Off, it is a plain box. */
  public autocompleteEnabled: boolean = false;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    brand: 'number[]', active: 'boolean', unallocated: 'boolean'
  };
  private screenFilterUx: ScreenFilterUx;
  constructor(
    private successModalHelper: SuccessModalHelperService,
    private studentService: StudentService,
    private router: Router,
    private featureHelperService: FeatureHelperService,
    public themeService: ThemeService,
    private studentMaintenanceService: StudentMaintenanceService,
    public dialog: MatDialog,
    private errorModalHelper: ErrorModalHelperService,
    private brandFilterService: BrandFilterService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null,
    @Optional() public clientSuggestions: ClientSuggestionsService = null
  ) {
    this.screenFilterUx = new ScreenFilterUx(FeatureSwitchKeys.filtersLearnerSearch, StudentSearchComponent.filterSpec, this.router, this.route);
  }

  ngOnInit() {
    setTimeout(() => {
      this.searchPlaceholder = "Learner Search";
      this.studentTooltip = "Please enter a learner name or Id";
    }, 100);

    let featureList = new Array<string>();
    featureList.push(
      "StudentDataView",
      "StudentSearchClientFilter",
      "GenerateStudentAccess");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (currentUserFeatures.filter(f => f.feature === "StudentDataView")[0].enabled) {
      this.hasStudentViewPermission = true;
    }
    if (currentUserFeatures.filter(f => f.feature === "GenerateStudentAccess")[0].enabled) {
      this.hasGenerateStudentAccessPermission = true;
    }
    if (currentUserFeatures.filter(f => f.feature === "StudentSearchClientFilter")[0].enabled) {
      this.hasStudentSearchClientFilterPermission = true;
    }

    this.studentMaintenanceService.studentId = 0;

    this.buildColumns();

    this.featureSwitches.isEnabled(FeatureSwitchKeys.searchAutocompleteLearnerSearch)
      .subscribe(enabled => this.autocompleteEnabled = enabled);
    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersLearnerSearch).subscribe(enabled => {
      this.filtersEnabled = enabled;
      if (!enabled) {
        return;
      }
      this.buildColumns();
      this.brandFilterService.getMyBrands().subscribe(brands => {
        this.brands = brands ?? [];
        this.buildColumns();
      });
      this.studentService.getSearchKeyStages().subscribe(keyStages => {
        this.keyStageOptions = (keyStages ?? []).map(keyStage => ({ keyStage }));
      });
      // A shared link's filters, else this user's last ones, then the search runs as it would on Search.
      const initial = this.filterUx ? this.screenFilterUx.initialState() : null;
      if (initial) {
        this.applyFilterState(initial);
        this.search();
      }
    });
  }

  private buildColumns(): void {
    const label = "Learner";
    const brandColumn: ColumnDef<SearchStudent>[] = this.filtersEnabled && this.brands.length > 1
      ? [{ field: 'companyId', header: 'Brand', type: 'template' }]
      : [];
    const keyStageColumn: ColumnDef<SearchStudent>[] = this.filtersEnabled
      ? [{ field: 'keyStages', header: 'Key stage', type: 'template' }]
      : [];
    this.columns = [
      { field: 'studentId', header: label + ' Id' },
      ...brandColumn,
      { field: 'studentName', header: label + ' Name', type: 'template' },
      { field: 'clientName', header: 'Client Name', type: 'template' },
      { field: 'upn', header: 'UPN', type: 'template' },
      { field: 'mainSubject', header: 'Main Subject', type: 'template' },
      { field: 'yearGroup', header: 'Year Group', type: 'template' },
      ...keyStageColumn,
      { field: 'actions', header: 'Actions', type: 'template' }
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    this.filterStudentSearchResults();
  }

  public search(): void {
    this.showGrantStudentAccess = false;
    if (this.filtersEnabled && this.filterUx) {
      this.screenFilterUx.save(this.filterState());
    }
    // New parameters only when the switch is on; off sends the pre-filter request body.
    const filters = this.filtersEnabled
      ? { companyIds: this.companyIds, hasActiveBooking: this.hasActiveBooking, keyStages: this.keyStages ?? [] }
      : null;
    this.studentService.searchStudents(this.searchText, this.clientFilter, this.clientId, this.unallocatedOnlyFilter, this.allocationSearch, filters).subscribe((response: SearchStudent[]) => {

      this.searchStudentResults = response ?? new Array<SearchStudent>();
      this.searchClicked = true;

      this.filterStudentSearchResults();

    }, error => {
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });

  }

  /** Clear all: back to the screen's defaults, forgotten, and out of the URL. The results are left as they are. */
  public clearAll(): void {
    this.searchText = '';
    this.clientFilter = '';
    this.applyFilterState({});
    this.screenFilterUx.clear();
  }

  private filterState(): FilterState {
    return {
      brand: this.companyIds,
      active: this.hasActiveBooking,
      unallocated: this.unallocatedOnlyFilter || null
    };
  }

  private applyFilterState(state: FilterState): void {
    this.companyIds = (state.brand as number[]) ?? [];
    this.hasActiveBooking = typeof state.active === 'boolean' ? state.active : null;
    this.unallocatedOnlyFilter = state.unallocated === true;
  }

  public emitAddStudentEvent(student: SearchStudent): void {
    this.addStudent.emit(student);
  }

  public viewStudent(student: SearchStudent): void {
    this.studentMaintenanceService.studentId = student.studentId;
    // this.router.navigate(['/view-student']);
    const dialogRef = this.dialog.open(ManualAddModalComponent, {
      panelClass: 'my-dialog'
    });
  }

  public generateAccess(student: SearchStudent): void {
    let data = new GenerateStudentAccessModalData();
    data.studentId = student.studentId;
    data.studentName = student.studentDetailItem.firstName + " " + student.studentDetailItem.surname;
    data.showSignUpEmailButton = student.studentDetailItem.emailAddress == null ? false : true;
    data.showWebsiteLinkButton = true;

    const dialogRef = this.dialog.open(GenerateStudentAccessModalComponent, {
      data: data,
      panelClass: 'my-dialog'
    });
  }

  private filterStudentSearchResults(): void {
    if (this.excludeStudentIds && this.searchStudentResults.length > 0) {
      this.filteredStudents = this.searchStudentResults.filter(r => {
        let index = this.excludeStudentIds.findIndex(e => e == r.studentId);
        return index == -1;
      });
    } else {
      this.filteredStudents = this.searchStudentResults;
    }
  }

}
